import { DeleteOutlined, UserAddOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { mainActions } from "../../store/main/mainSlice";
import { MutableRefObject, useEffect, useState } from "react";
import CustomersModal from "./Modals/Customers/CustomersModal";
import Item from "./Modals/Items/Item";
import { useTranslation } from "react-i18next";
import { SaleSpecification } from "../../api/sales";

type SaleDetail = {
  itemId: number;
  barcode: string;
  qty: number | null;
  cost: number | null;
  price: number;
  isTaxable: boolean;
  tax: number;
  discount: number | null;
  notes: string | null;
  specifications: SaleSpecification[];
};

export const handleMakePayment = async (
  hold: boolean,
  onHoldDate: string | null,
  items: SaleDetail[],
  createSaleMutation: any,
  customer: { id: number },
  discount: { amount: number; type: string },
  mainCurrency: { id: number },
  sessionId: React.MutableRefObject<string | null>,
  invoiceNo: string,
  exchangeRate: string,
  deliveryCharge?: number | null,
  deliveryId?: number | null
) => {
  const detailsFormatted = items.map((item: SaleDetail) => {
    const saleSpecifications = item.specifications.map(
      (spec: SaleSpecification) => ({
        isComponent: spec.isComponent,
        specificationId: spec.specificationId,
        extraCharge: spec.extraCharge,
        quantity: spec.quantity,
        notes: spec.notes,
        exchangeRate: spec.extraCharge,
      })
    );

    return {
      id: item.itemId,
      barcode: item.barcode,
      quantity: item.qty || null,
      cost: item.cost,
      price: item.price,
      isTaxable: item.isTaxable ?? false,
      tax: item.tax,
      discount: item.discount || null,
      notes: item.notes || null,
      specifications: saleSpecifications,
      exchangeRate: exchangeRate,
    };
  });

  const currentDate = new Date();
  const year = String(currentDate.getFullYear());
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getDate()).padStart(2, "0");
  const hours = String(currentDate.getHours()).padStart(2, "0");
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");
  const seconds = String(currentDate.getSeconds()).padStart(2, "0");
  const formattedDate = `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;

  return createSaleMutation({
    warehouseId: 1, //  static                                  done
    invoiceNo: invoiceNo,
    sessionId: sessionId.current,
    workstationId: 1, // static for now                         done
    customerId: customer?.id, //mandatory                       done
    paymentModeId: 1, //   static                               done
    invoiceTypeId: deliveryId ? 2 : 1,
    date: formattedDate, // current                             done
    dueDate: formattedDate, // current                          done
    isTaxable: false, // false for now                          done
    tax: null, // null since isTaxable false                    done
    discountType: discount.type, // Frontend                    done
    discount: discount.amount === 0 ? null : discount.amount, // Frontend                      done
    currencyId: mainCurrency.id, // main currency               done
    exchangeRate: exchangeRate, // ----                                  pending
    onHold: hold,
    onHoldDate: onHoldDate,
    taxExchangeRate: null, // null since isTaxable false        done
    isApproved: true, // static true                            done
    approvedDate: formattedDate, // current date                done
    isDelivered: false, // static                               done
    deliveredDate: null, // null since isDelivered is falsed    done
    isPaid: false, // static true                                done
    paidDate: null, // currents date                   done
    isPosted: false, // static false                            done
    postedDate: null, // static null                            done
    notes: null, // something                   ez
    details: detailsFormatted,
    deliveryCharge: deliveryCharge ? deliveryCharge : null,
    deliveryId: deliveryId ? deliveryId : null,
  });
};

export const handleFinalizePayment = async (
  hold: boolean,
  items: SaleDetail[],
  createRecieptsMutation: any,
  customer: { id: number },
  discount: { amount: number; type: string },
  mainCurrency: { id: number },
  tenderedLBP: number,
  tenderedUSD: number,
  changeLBP: string,
  changeUSD: string,
  exchangeRate: number,
  sessionId: MutableRefObject<string | null>,
  invoiceNo: string,
  isPaid: boolean,
  ReduxDeliveryCharge: number,
  ReduxDeliveryLocation: {
    location: string,
    id: number
  },
  ReduxDeliveryChargeText: string
) => {
  const detailsFormatted = items.map((item: SaleDetail) => {
    const saleSpecifications = item.specifications.map(
      (spec: SaleSpecification) => ({
        isComponent: spec.isComponent,
        specificationId: spec.specificationId,
        extraCharge: spec.extraCharge,
        quantity: spec.quantity,
        notes: spec.notes,
      })
    );
    return {
      id: item.itemId,
      barcode: item.barcode,
      quantity: item.qty || null,
      cost: item.cost,
      price: item.price,
      isTaxable: item.isTaxable ?? false,
      tax: item.tax,
      discount: item.discount || null,
      notes: "Notes",
      specifications: saleSpecifications,
      exchangeRate: exchangeRate,
    };
  });

  const currentDate = new Date();
  const year = String(currentDate.getFullYear());
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getDate()).padStart(2, "0");
  const hours = String(currentDate.getHours()).padStart(2, "0");
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");
  const seconds = String(currentDate.getSeconds()).padStart(2, "0");
  const formattedDate = `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
  const receipts = [];

  const amount = localStorage.getItem("amount");
  const secondaryCurrencyLS = localStorage.getItem("secondaryCurrency");
  if (!amount || !secondaryCurrencyLS) return;

  let { fp, ut, lt } = JSON.parse(amount);

  const secondaryCurrency = JSON.parse(secondaryCurrencyLS);

  const ru = fp - ut;
  const rl = ru * exchangeRate;
  isPaid = fp <= ut || fp <= lt / exchangeRate || rl <= lt;

  if (fp <= ut) {
    receipts.push({
      paymentModeId: 1,
      date: formattedDate,
      dueDate: formattedDate,
      reference: null,
      amount: fp,
      currencyId: mainCurrency.id,
      exchangeRate: 1,
      isPosted: false,
      postedDate: null,
      sessionId: sessionId.current,
      notes: null,
    });
  } else {
    if (ut) {
      receipts.push({
        paymentModeId: 1,
        date: formattedDate,
        dueDate: formattedDate,
        reference: null,
        amount: ut,
        currencyId: mainCurrency.id,
        exchangeRate: 1,
        isPosted: false,
        postedDate: null,
        sessionId: sessionId.current,
        notes: null,
      });
    }
    if (lt) {
      receipts.push({
        paymentModeId: 1,
        date: formattedDate,
        dueDate: formattedDate,
        reference: null,
        amount: rl <= lt ? rl : lt,
        currencyId: secondaryCurrency.id,
        exchangeRate: exchangeRate,
        isPosted: false,
        postedDate: null,
        sessionId: sessionId.current,
        notes: null,
      });
    }
  }

  return createRecieptsMutation({
    customerId: customer?.id, //mandatory                       done
    currencyId: mainCurrency.id, // main currency               done
    date: formattedDate, // current                             done
    deliveredDate: null, // null since isDelivered is falsed    done
    details: detailsFormatted,
    discount: discount.amount === 0 ? null : discount.amount, // Frontend                      done
    discountType: discount.type, // Frontend                    done
    dueDate: formattedDate, // current                          done
    exchangeRate: 1, // ----                                  pending
    invoiceNo: invoiceNo,
    invoiceTypeId: ReduxDeliveryCharge ? 2 : 1,
    isApproved: true, // static true                            done
    isDelivered: false, // static                               done
    isPaid: isPaid, // static true                                done
    isPosted: false, // static false                            done
    isTaxable: false, // false for now                          done
    notes: "Notes", // something                   ez
    onHold: hold,
    paidDate: formattedDate, // currents date                   done
    paymentModeId: 1, //   static                               done
    postedDate: null, // static null                            done
    receipts: receipts,
    sessionId: sessionId.current,
    tax: null, // null since isTaxable false                    done
    taxExchangeRate: null, // null since isTaxable false        done
    warehouseId: 1, //  static                                  done
    workstationId: 1, // static for now                         done
    deliveryCharge: ReduxDeliveryCharge,
    deliveryId: ReduxDeliveryLocation?.id
  });
};

const CheckoutNew = () => {
  const [t] = useTranslation("common");

  const items = useSelector((state: any) => state.main.items);
  const totalPrice = useSelector((state: any) => state.main.totalPrice);
  const customer = useSelector((state: any) => state.main.customer);
  const discount = useSelector((state: any) => state.main.discount);

  const dispatch = useDispatch();
  const mainCurrencyString = localStorage.getItem("mainCurrency");
  const taxRateString = localStorage.getItem("taxRate");
  const mainCurrency = mainCurrencyString
    ? JSON.parse(mainCurrencyString)
    : null;
  const taxRate = taxRateString ? JSON.parse(taxRateString).taxRate : null;

  const [currAbv, setCurrAbv] = useState(mainCurrency?.abbreviation);
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);

  useEffect(() => {
    if (items.length === 0) {
      dispatch(mainActions.removeCustomer());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    setCurrAbv(mainCurrency?.abbreviation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCurrencyString]);

  const calculateExtras = (): number => {
    let total = 0;
    items.map((item: SaleDetail) =>
      item.specifications.map((spec) => (total += spec.extraCharge))
    );
    return total;
  };

  return (
    <>
      <Col span={24} style={{ cursor: "copy" }}>
        <Row className="checkout-block" style={{ height: "45vh" }}>
          <Col span={24}>
            <Typography style={{ fontSize: "20px", fontWeight: "700" }}>
              {t("invoice")} #003
            </Typography>
          </Col>
          <Col
            span={24}
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              gap: "12px",
            }}
          >
            <div
              className=""
              style={
                items.length === 0
                  ? { cursor: "not-allowed" }
                  : { cursor: "pointer" }
              }
              onClick={() => {
                items.length !== 0 && setIsCustomerOpen(true);
              }}
            >
              <UserAddOutlined
                style={
                  items.length !== 0
                    ? { fontSize: "20px", marginRight: "10px", color: "#000" }
                    : {
                      color: "#9e9e9e",
                      fontSize: "20px",
                      marginRight: "10px",
                    }
                }
              />
              <Typography
                style={
                  items.length !== 0
                    ? { fontSize: "20px", fontWeight: "400", display: "inline" }
                    : {
                      fontSize: "20px",
                      fontWeight: "400",
                      display: "inline",
                      color: "#9e9e9e",
                    }
                }
              >
                {customer?.name ? (
                  <span style={{ color: "#000" }}> {customer?.name} </span>
                ) : (
                  `${t("new-customer")}`
                )}
              </Typography>
            </div>
            <Typography
              style={
                customer?.name
                  ? {
                    color: "red",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }
                  : {
                    color: "#9e9e9e",
                    fontSize: "12px",
                    fontWeight: "500",
                    cursor: "not-allowed",
                  }
              }
              onClick={() => {
                customer?.name && dispatch(mainActions.removeCustomer());
              }}
            >
              {t("remove-cutomer")}
            </Typography>
          </Col>
          {/* ----------------------------- */}
          <div className="" style={{ overflowY: "scroll", height: "35vh" }}>
            {items.map((row: any) => (
              <ItemRow row={row} key={row.localId} />
            ))}
          </div>
          {/* ----------------------------- */}
        </Row>
        <Row
          className="checkout-block"
          style={{
            minHeight: "32vh",
            marginTop: "10px",
          }}
        >
          <Col span={24} className="checkout-row">
            <Typography className="checkout-txt">{t("subtotal")}:</Typography>
            <Typography className="checkout-txt">
              {(totalPrice + calculateExtras()).toFixed(2)} {currAbv}
            </Typography>
          </Col>
          <Col span={24} className="checkout-row">
            <Typography className="checkout-txt">{t("tax")}:</Typography>
            <Typography className="checkout-txt">
              {(totalPrice * taxRate).toFixed(2)}
            </Typography>
          </Col>
          <Col span={24} className="checkout-row">
            <Typography className="checkout-txt">{t("discount")}:</Typography>
            <Typography className="checkout-txt">
              {discount?.amount || 0}
              {discount?.type === "%" ? "%" : `${mainCurrency?.symbol || ""}`}
            </Typography>
          </Col>
          <Col span={24} className="checkout-row">
            <Typography className="checkout-txt-primary">
              {t("total")}:
            </Typography>
            {
              <Typography className="checkout-txt-primary">
                {discount.type === "%"
                  ? (
                    (totalPrice + calculateExtras()) *
                    ((100 - discount.amount) / 100)
                  ).toFixed(2)
                  : (totalPrice + calculateExtras() - discount.amount).toFixed(
                    2
                  )}
                {currAbv}
              </Typography>
            }
          </Col>
          <Col offset={1} span={23} className="checkout-row">
            <Typography className="checkout-txt">
              {t("exchange-rate")}
            </Typography>
            <Typography className="checkout-txt">100 USD</Typography>
          </Col>
          <Col offset={1} span={23} className="checkout-row">
            <Typography className="checkout-txt">
              {t("total-in-lbp")}
            </Typography>
            <Typography className="checkout-txt">100.000.000 LBP</Typography>
          </Col>

          <Col span={24} style={{ marginTop: "30px" }}>
            <Button
              type="primary"
              style={{ height: "50px", fontSize: "24px" }}
              block
            >
              {t("proceed-with-payment")}
            </Button>
          </Col>
        </Row>
      </Col>
      <CustomersModal
        isCustomerOpen={isCustomerOpen}
        setIsCustomerOpen={setIsCustomerOpen}
      />
    </>
  );
};

export default CheckoutNew;

const ItemRow = ({ row }: any) => {
  const dispatch = useDispatch();
  const [isItemSpecificationsOpen, setIsItemSpecificationsOpen] =
    useState(false);

  const [selectedItem, setSelectedItem] = useState<any>();
  return (
    <>
      <Col
        span={24}
        style={{ padding: "10px 0", cursor: "pointer" }}
        onClick={() => {
          setSelectedItem(row);
          setIsItemSpecificationsOpen(true);
          dispatch(mainActions.selectedItemId(row.localId));
        }}
      >
        <Row align="middle">
          <Col span={13}>
            <Typography style={{ fontSize: "15px", fontWeight: "500" }}>
              {row?.name}
            </Typography>
          </Col>
          <Col span={2}>
            <Input
              min={"1"}
              max="100000"
              value={row.qty}
              style={{ height: "30px" }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onBlur={(e) => {
                const inputValue = e.target.value;
                if (Number(inputValue) === 0) {
                  dispatch(
                    mainActions.setQty({
                      id: row.localId,
                      qty: 1,
                    })
                  );
                }
              }}
              onChange={(e) => {
                const inputValue = e.target.value;
                const numericValue = /^\d+$/.test(inputValue)
                  ? inputValue
                  : null;
                const limitedValue = Math.min(Number(numericValue), 100000);
                dispatch(
                  mainActions.setQty({
                    id: row.localId,
                    qty: limitedValue,
                  })
                );
              }}
            />
          </Col>
          <Col offset={1} span={5}>
            <Typography style={{ fontSize: "15px", fontWeight: "500" }}>
              {((row.price + row.extraCharge) * row.qty).toFixed(2)}
            </Typography>
          </Col>
          <Col span={1}>
            <DeleteOutlined
              onClick={() =>
                dispatch(mainActions.deleteItem({ id: row.localId }))
              }
              className="item-delete-btn"
            />
          </Col>
        </Row>
      </Col>
      <Item
        isOpen={isItemSpecificationsOpen}
        setIsOpen={setIsItemSpecificationsOpen}
        selectedItem={selectedItem}
      />
    </>
  );
};
